import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, useMotionValue, useAnimation } from 'framer-motion';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { serviceApiService, Service } from '../services/serviceApi';

const ServicesSection: React.FC = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [duplicatedServices, setDuplicatedServices] = useState<Service[]>([]);
  const [isPaused, setIsPaused] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const x = useMotionValue(0);
  const controls = useAnimation();
  const containerRef = useRef<HTMLDivElement>(null);

  const loadServices = async () => {
    try {
      setLoading(true);
      const servicesData = await serviceApiService.getServices();
      setServices(Array.isArray(servicesData) ? servicesData : []);
    } catch (err: any) {
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const startInfiniteScroll = useCallback(async () => {
    if (isPaused || !isMounted || services.length === 0) return;
    
    const cardWidth = 400;
    const totalWidth = cardWidth * services.length;
    
    await controls.start({
      x: -totalWidth,
      transition: {
        duration: services.length * 5,
        ease: "linear",
        repeat: Infinity,
      }
    });
  }, [isPaused, isMounted, services.length, controls]);

  const pauseScroll = () => {
    setIsPaused(true);
    controls.stop();
  };

  const resumeScroll = useCallback(() => {
    if (!isMounted) return;
    setIsPaused(false);
  }, [isMounted]);

  useEffect(() => {
    setIsMounted(true);
    loadServices();
  }, []);

  useEffect(() => {
    if (services.length > 0 && isMounted) {
      // Ne dupliquer que s'il y a assez de services pour justifier un carrousel infini
      // Sinon, afficher simplement les services sans duplication
      const duplicated = services.length >= 4 
        ? [...services, ...services, ...services]
        : services;
      setDuplicatedServices(duplicated);
    }
  }, [services, isMounted]);

  useEffect(() => {
    if (services.length > 0 && isMounted && !isPaused) {
      startInfiniteScroll();
    }
  }, [services.length, isMounted, isPaused, startInfiniteScroll]);

  const handlePrevious = () => {
    if (!isMounted) return;
    const currentX = x.get();
    controls.start({
      x: currentX + 400,
      transition: { duration: 0.5, ease: [0.19, 1, 0.22, 1] }
    });
  };

  const handleNext = () => {
    if (!isMounted) return;
    const currentX = x.get();
    controls.start({
      x: currentX - 400,
      transition: { duration: 0.5, ease: [0.19, 1, 0.22, 1] }
    });
  };

  if (loading) return (
    <section className="relative py-24 bg-gradient-to-br from-gray-50 via-white to-amber-50/30 overflow-hidden">
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center">
          <div className="inline-block animate-pulse text-amber-400">Chargement des services...</div>
        </div>
      </div>
    </section>
  );
  
  if (services.length === 0) return null;

  return (
    <section className="relative py-24 bg-gradient-to-br from-blue-950 via-blue-900 to-slate-900 overflow-hidden">
      {/* Effets de fond */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-72 h-72 bg-amber-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-amber-500/5 to-orange-500/5 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.div
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 3, repeat: Infinity }}
            className="inline-block mb-4"
          >
            <span className="text-amber-400 font-light text-sm uppercase tracking-[0.4em]">
              ✦ Excellence ✦
            </span>
          </motion.div>
          <h2 className="text-5xl md:text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-amber-400 to-amber-500 mb-4 tracking-tight">
            Nos Services
          </h2>
          <div className="w-24 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent mx-auto" />
        </div>

        {/* Carrousel */}
        <div 
          className="relative max-w-7xl mx-auto overflow-hidden" 
          ref={containerRef}
          onMouseEnter={pauseScroll}
          onMouseLeave={resumeScroll}
        >
          <motion.div
            className="flex gap-6 cursor-grab active:cursor-grabbing"
            style={{ x }}
            animate={controls}
            drag="x"
            dragConstraints={{ left: -400 * services.length, right: 0 }}
            dragElastic={0.2}
            dragTransition={{ bounceStiffness: 200, bounceDamping: 20 }}
            onDragStart={pauseScroll}
            onDragEnd={(_, info) => {
              const swipeThreshold = 50;
              if (Math.abs(info.offset.x) > swipeThreshold) {
                if (info.offset.x > 0) handlePrevious();
                else handleNext();
              }
              resumeScroll();
            }}
          >
            {duplicatedServices.map((service, index) => (
              <motion.div
                key={`${service.id}-${index}`}
                initial={{ opacity: 1, scale: 1 }}
                whileHover={{ scale: 1.03, y: -10 }}
                className="flex-shrink-0 w-[350px] cursor-pointer"
                onClick={() => navigate('/services')}
              >
                <div className="relative group h-full">
                  {/* Card style IA */}
                  <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-500 hover:-translate-y-2 h-full flex flex-col">
                    {/* Nom en gold et bold */}
                    <h3 className="text-2xl font-bold text-white group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-amber-300 group-hover:to-amber-500 mb-3 tracking-tight transition-all duration-300">
                      {service.name}
                    </h3>

                    {/* Description */}
                    <p className="text-gray-300 font-light mb-6 text-sm leading-relaxed min-h-[3rem]">
                      {service.description || 'Service professionnel de qualité'}
                    </p>

                    {/* Prix */}
                    <div className="mb-4">
                      <div className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-amber-400 to-amber-500">
                        {service.price_display || 'Sur devis'}
                      </div>
                    </div>

                    {/* Détails */}
                    <div className="space-y-2 mb-6 flex-grow">
                      <div className="flex items-start text-xs text-gray-400 font-light">
                        <span className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-1.5 mr-2 flex-shrink-0" />
                        Solution personnalisée
                      </div>
                      <div className="flex items-start text-xs text-gray-400 font-light">
                        <span className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-1.5 mr-2 flex-shrink-0" />
                        Support inclus
                      </div>
                      {service.price >= 50000 && (
                        <div className="flex items-start text-xs text-gray-400 font-light">
                          <span className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-1.5 mr-2 flex-shrink-0" />
                          Paiement échelonné sur plusieurs mois
                        </div>
                      )}
                    </div>

                    {/* Bouton */}
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-6 py-3 rounded-xl font-medium hover:shadow-lg transition-all duration-300 flex items-center justify-center space-x-2 w-full"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate('/services');
                      }}
                    >
                      <span>Découvrir</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Boutons navigation */}
          <motion.button
            whileHover={{ scale: 1.1, x: -5 }}
            whileTap={{ scale: 0.9 }}
            onClick={handlePrevious}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-12 h-12 bg-white/10 backdrop-blur-md border border-white/20 rounded-full flex items-center justify-center text-amber-400 hover:bg-gradient-to-br hover:from-amber-500 hover:to-amber-600 hover:text-white hover:border-amber-400 transition-all duration-300 z-10 shadow-lg hover:shadow-xl hover:shadow-amber-500/30"
          >
            <ChevronLeft className="w-5 h-5" />
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.1, x: 5 }}
            whileTap={{ scale: 0.9 }}
            onClick={handleNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-12 h-12 bg-white/10 backdrop-blur-md border border-white/20 rounded-full flex items-center justify-center text-amber-400 hover:bg-gradient-to-br hover:from-amber-500 hover:to-amber-600 hover:text-white hover:border-amber-400 transition-all duration-300 z-10 shadow-lg hover:shadow-xl hover:shadow-amber-500/30"
          >
            <ChevronRight className="w-5 h-5" />
          </motion.button>
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <button
            onClick={() => navigate('/services')}
            className="text-amber-400 hover:text-amber-500 font-bold text-lg tracking-wider transition-colors"
          >
            Voir tous nos services →
          </button>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
